import React, { useState, useEffect, useContext } from "react";
import { Modal, Table, Tooltip, Select, Button, message, Spin } from "antd";
import { platformApi } from "../../../../../api";
import moment from "moment-timezone";
import { formatCallDate, formatCallTime } from "../../../../../utils/callLogTime";
import CallReceivedIcon from "@material-ui/icons/CallReceived";
import CallMadeIcon from "@material-ui/icons/CallMade";
import { getUUID } from "../../../../../_helpers/utils";

const { Option } = Select;

const CallHistoryModal = ({
  visible,
  onClose,
  selectedCall,
  currentDirection,
  currentStatus,
}) => {
  const telecmiUser = localStorage.getItem("selectedTelecmiUserId");
  const [callHistory, setCallHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationFilters, setPaginationFilters] = useState({
    direction: currentDirection || "",
    status: currentStatus || "",
    page: 1,
    size: 20,
  });
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchCallHistory = async (customFilters = paginationFilters) => {
    if (!selectedCall) return;

    const phoneNumber =
      selectedCall.direction === "inbound"
        ? selectedCall.from.slice(2)
        : selectedCall.to.slice(2);

    setLoading(true);
    try {
      const response = await platformApi.post(
        `/api/telecmi/callHistoryForNumber/${phoneNumber}`,
        customFilters
      );

      setCallHistory(response.data.data || []);
      setTotal(response.data.pagination?.totalRecords || 0);
    } catch (error) {
      message.error("Failed to fetch call history");
      console.error("Error fetching call history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && selectedCall) {
      const initialFilters = {
        direction: currentDirection || "",
        status: currentStatus || "",
        page: 1,
        size: limit,
      };
      setPaginationFilters(initialFilters);
      fetchCallHistory(initialFilters);
    }
  }, [visible, selectedCall]);

  // Get unique user names from callHistory data
  const uniqueUsers = [
    ...new Set(callHistory.map((item) => item.user_name || "Unknown")),
  ];

  const handleAttendedByChange = (value) => {
    const updatedFilters = { ...paginationFilters, attendedBy: value };
    setPaginationFilters(updatedFilters);
  };

  // Filter data based on attendedBy filter
  const filteredData = React.useMemo(() => {
    if (!paginationFilters.attendedBy) {
      return callHistory;
    }
    return callHistory.filter((item) => {
      const userName = item.user_name || "Unknown";
      return userName === paginationFilters.attendedBy;
    });
  }, [callHistory, paginationFilters.attendedBy]);

  const viewModuleDetails = (record) => {
    console.log("record:", record);

    if (
      !record.moduleid &&
      record.module !== "Call-Back" &&
      record.module !== "new_customer"
    ) {
      message.info("No module details available for this record");
      return;
    }
    const phone =
      record.direction == "outbound"
        ? record.to.slice(2)
        : record.from.slice(2);
    switch (record.module) {
      case "new_customer":
        message.info("Call Query from New customer");
        break;
      case "jobOrder":
      case "Job-Order":
        localStorage.setItem(
          "jobOrderState",
          JSON.stringify({ jobId: record.moduleid })
        );
        window.open("/autoadmin/job_order", "_blank");
        break;
      case "Booking":
        localStorage.setItem(
          "bookingState",
          JSON.stringify({ addFlag: true, id: record.moduleid })
        );
        window.open("/autoadmin/booking_register", "_blank");
        break;
      case "Customer":
        localStorage.setItem(
          "customerState",
          JSON.stringify({ phone: phone, editable: true })
        );
        window.open("/autoadmin/customer_details", "_blank");
        break;
      case "Quotation":
      case "Follow-Ups":
        window.open(
          `/autoadmin/follow-ups/${phone}/fupdate/${record.moduleid}`,
          "_blank"
        );
        break;
      case "Call-Back":
        message.info(
          `Call Back for missed call from ${record.user_name} to ${record.customer_name}`
        );
        break;
      case "Insurance":
        var route = `/autoadmin/insurance_renewal_reminder/${record.moduleid}`;
        window.open(route, "_blank");
        break;
      case "Other insurance":
        window.open(
          `/autoadmin/other_insurance_renewal_reminder/${record.moduleid}`,
          "_blank"
        );
        break;
      case "Service":
        window.open(
          `/autoadmin/service-followups/${phone}/fupdate/${record.moduleid}`,
          "_blank"
        );
        break;
      default:
        message.info("No module details available for this record");
        break;
    }
  };

  const columns = [
    {
      title: "Date",
      key: "date",
      render: (_, record) => formatCallDate(record),
    },
    {
      title: "Time",
      key: "time",
      render: (_, record) => formatCallTime(record),
    },
    {
      title: "Customer Number",
      key: "number",
      render: (_, record) => {
        const isInbound = record.direction === "inbound";
        const phoneNumber = isInbound ? record.from : record.to;
        const isMissed = record.status === "missed";

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {isInbound ? (
              <Tooltip title="Incoming Call">
                <CallReceivedIcon
                  style={{ color: isMissed ? "red" : "green", fontSize: 16 }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Outgoing Call">
                <CallMadeIcon
                  style={{ color: isMissed ? "red" : "blue", fontSize: 16 }}
                />
              </Tooltip>
            )}
            <span>{phoneNumber || "-"}</span>
          </div>
        );
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => text || "Unknown Number",
    },
    {
      title: "Employee Name",
      dataIndex: "user_name",
      key: "user_name",
      render: (text) => text || "Unknown",
    },
    {
      title: "Hangup Reason",
      dataIndex: "hangup_reason",
      key: "hangup_reason",
      render: (reason) => {
        const reasonMap = {
          sent_bye: "Call ended by customer",
          recv_bye: "Call ended by employee",
          recv_cancel: "Call unanswered",
          sent_reject: "Call rejected by customer",
          normal: "Call ended normally",
        };

        return reasonMap[reason] || "No reason provided";
      },
    },
    {
      title: "Module",
      key: "module",
      render: (record) => {
        const isValidModule = record.module !== "null" && record.module;

        return (
          <span
            className={isValidModule ? "linkylink" : ""}
            style={{ cursor: isValidModule ? "pointer" : "not-allowed" }}
            onClick={() => {
              if (isValidModule) viewModuleDetails(record);
            }}
          >
            {isValidModule ? record.module : "-"}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "bucketURL",
      key: "bucketURL",
      width: 250,
      align: "center",
      render: (url, record) => {
        const phoneNumber =
          record.direction === "inbound" ? record.from : record.to;
        const isMissed = record.status === "missed";
        return isMissed ? (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              if (!telecmiUser) {
                message.error(
                  "Can't place call, Login to telecmi account to continue"
                );
                return;
              }
              const UniqueId = getUUID();
              setMobileNumber(phoneNumber);
              piopiyDialUser(
                piopiy,
                phoneNumber,
                UniqueId,
                "Call-Back",
                phoneNumber
              );
            }}
          >
            Call Back
          </Button>
        ) : (
          <audio
            controls
            style={{
              width: "170px",
              height: "40px",
              transform: "scale(0.8)",
            }}
          >
            <source src={url} type="audio/mpeg" />
            Your browser does not support the audio element.
          </audio>
        );
      },
    },
  ].filter(Boolean);

  const getPhoneNumber = () => {
    if (!selectedCall) return "";
    return selectedCall.direction === "inbound"
      ? selectedCall.from.slice(2)
      : selectedCall.to.slice(2);
  };

  return (
    <Modal
      title={`Call History - ${getPhoneNumber()} (${
        selectedCall?.customer_name || "Unknown"
      })`}
      visible={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Select
            style={{ width: 200 }}
            placeholder="Filter by Attended By"
            onChange={handleAttendedByChange}
            value={paginationFilters.attendedBy}
            allowClear
          >
            <Option value="">All</Option>
            {uniqueUsers.map((user) => (
              <Option key={user} value={user}>
                {user}
              </Option>
            ))}
          </Select>
        </div>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            current: paginationFilters.page,
            pageSize: limit,
            total: filteredData.length,
            showQuickJumper: true,
            onChange: (page) => {
              const updatedFilters = { ...paginationFilters, page };
              setPaginationFilters(updatedFilters);
            },
          }}
          loading={loading}
        />
      </div>
    </Modal>
  );
};

export default CallHistoryModal;
